import React, { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import api from "../lib/api";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminSlots() {
    const [slots, setSlots] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = () => api.get("/admin/slots").then((r) => setSlots(r.data));
    useEffect(() => { load(); }, []);

    const handleDelete = async (id) => {
        if (!window.confirm("Eliminare questo slot?")) return;
        try {
            await api.delete(`/admin/slots/${id}`);
            toast.success("Slot eliminato");
            load();
        } catch { toast.error("Errore"); }
    };

    const grouped = slots.reduce((acc, s) => {
        (acc[s.service] ||= []).push(s);
        return acc;
    }, {});

    return (
        <div className="flex min-h-screen bg-ink" data-testid="admin-slots-page">
            <AdminSidebar />
            <main className="flex-1 p-10">
                <div className="flex items-start justify-between mb-8">
                    <div>
                        <div className="text-gold text-xs tracking-[0.4em] uppercase">Gestione</div>
                        <h1 className="font-serif text-5xl text-white mt-2">Slot Orari</h1>
                        <p className="text-gray-400 text-sm mt-2">Configura gli orari disponibili per pranzo e cena.</p>
                    </div>
                    <button onClick={() => setCreating(true)} data-testid="add-slot-btn"
                        className="flex items-center gap-2 bg-gold text-black font-semibold px-5 py-3 rounded-sm hover:bg-gold-light transition-colors">
                        <Plus size={16} /> Nuovo slot
                    </button>
                </div>

                <div className="space-y-8">
                    {Object.entries(grouped).map(([service, items]) => (
                        <div key={service}>
                            <h2 className="font-serif text-2xl text-gold capitalize mb-4">{service}</h2>
                            <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
                                {items.map((s) => (
                                    <div key={s.slot_id} className="bg-ink-surface border border-white/10 p-4 rounded-sm group relative">
                                        <div className="font-serif text-3xl text-white">{s.time}</div>
                                        <div className="text-xs text-gray-500 mt-1 truncate">{s.label}</div>
                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                            <button onClick={() => setEditing(s)} className="p-1.5 text-gray-400 hover:text-gold bg-ink rounded-sm" data-testid={`edit-slot-${s.time.replace(":", "")}`}>
                                                <Edit3 size={12} strokeWidth={1.5} />
                                            </button>
                                            <button onClick={() => handleDelete(s.slot_id)} className="p-1.5 text-gray-400 hover:text-red-400 bg-ink rounded-sm" data-testid={`delete-slot-${s.time.replace(":", "")}`}>
                                                <Trash2 size={12} strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {(editing || creating) && (
                    <SlotDialog
                        slot={editing}
                        onClose={() => { setEditing(null); setCreating(false); }}
                        onSaved={() => { load(); setEditing(null); setCreating(false); }}
                    />
                )}
            </main>
        </div>
    );
}

function SlotDialog({ slot, onClose, onSaved }) {
    const [form, setForm] = useState({
        label: slot?.label || "",
        time: slot?.time || "",
        service: slot?.service || "pranzo",
    });

    const submit = async (e) => {
        e.preventDefault();
        try {
            if (slot) await api.put(`/admin/slots/${slot.slot_id}`, form);
            else await api.post("/admin/slots", form);
            toast.success("Salvato");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
                className="bg-ink-surface border border-white/10 rounded-sm p-8 max-w-md w-full space-y-4"
                data-testid="slot-dialog">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-serif text-2xl text-white">{slot ? "Modifica Slot" : "Nuovo Slot"}</h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
                </div>

                <Field label="Etichetta">
                    <input type="text" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                        placeholder="Es. Cena 20:00" required data-testid="slot-label-input"
                        className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                </Field>
                <Field label="Orario (HH:MM)">
                    <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                        required data-testid="slot-time-input"
                        className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                </Field>
                <Field label="Servizio">
                    <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}
                        data-testid="slot-service-input"
                        className="w-full px-3 py-2.5 bg-ink border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none">
                        <option value="pranzo">Pranzo</option>
                        <option value="cena">Cena</option>
                        <option value="aperitivo">Aperitivo</option>
                        <option value="brunch">Brunch</option>
                    </select>
                </Field>

                <button type="submit" className="w-full bg-gold text-black font-semibold py-3 rounded-sm hover:bg-gold-light tracking-widest uppercase text-sm" data-testid="slot-save-btn">
                    Salva
                </button>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
